import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createNewBookmarkApi, deleteBookmarkApi, fetchAllBookmarksApi } from "../../api/BookmarksApi";

const USER_ID = "1";

type BookmarksState = {
    fetched: boolean;
    list: any[];
    fetchingError: string;

    creatingDone: boolean | null;
    creatingError: string;

    deletingDone: boolean | null;
    deletingError: string;
}

const initialState: BookmarksState = {
    fetched: false,
    list: [],
    fetchingError: '',

    creatingDone: null,
    creatingError: '',

    deletingDone: null,
    deletingError: ''
}

const NEW_BOOKMARK_ID = -99;

export const bookmarksSlice = createSlice({
    name: 'bookmarks',
    reducers: {
        addNewBookmark: (state, action: PayloadAction<any>) => {
            state.list = [
                ...state.list,
                {
                    id: NEW_BOOKMARK_ID,
                    user_id: USER_ID,
                    thread_id: action.payload.thread_id,
                    entry_id: action.payload.entry_id
                }
            ]
        },
        deleteBookmark: (state, action: PayloadAction<any>) => {
            state.list = state.list.filter( (s: any) => s.id !== action.payload.bookmarkId)
        }
    },
    initialState: initialState,
    extraReducers: builder => {
        builder
            // fetcing all bookmarks
            .addCase(getAllBookmarks.pending, (state) => {
                state.fetched = false;
            })
            .addCase(getAllBookmarks.fulfilled, (state, action: any) => {
                state.fetched = true;
                state.list = action.payload;
            })
            .addCase(getAllBookmarks.rejected, state => {
                state.fetchingError = "Unable to load bookmarks.";
                state.fetched = true;
            })

            // creating bookmark
            .addCase(createBookmark.pending, (state) => {
                state.creatingDone = false;
            })
            .addCase(createBookmark.fulfilled, (state, action: any) => {
                state.creatingDone = true;
                state.list = [
                    ...state.list.filter((s: any) => s.id !== NEW_BOOKMARK_ID),
                    action.payload
                ]
            })
            .addCase(createBookmark.rejected, state => {
                state.creatingError = "Creating bookmark failed.";
                state.creatingDone = true;
            })

            // creating bookmark
            .addCase(removeBookmark.pending, (state) => {
                state.deletingDone = false;
            })
            .addCase(removeBookmark.fulfilled, (state, action: any) => {
                state.deletingDone = true;
            })
            .addCase(removeBookmark.rejected, state => {
                state.deletingError = "Deleting bookmark failed.";
                state.deletingDone = true;
            })
    }
})

export const createBookmark = createAsyncThunk(
    "bookmarks/createBookmark",
    async (request: any, { dispatch }) => {
        dispatch(addNewBookmark(request));
        return await createNewBookmarkApi({
            user_id: USER_ID,
            thread_id: request.thread_id,
            entry_id: request.entry_id
        });
    }
)

export const removeBookmark = createAsyncThunk(
    "bookmarks/removeBookmark",
    async (request: any, { getState, dispatch }) => {
        const state = getState() as any;
        const bookmark = state.bookmarks.list.find((s: any) => s.thread_id === request.thread_id
            && s.entry_id === request.entry_id)
        dispatch(deleteBookmark({bookmarkId: bookmark.id}));
        return await deleteBookmarkApi(bookmark.id);
    }
)

export const getAllBookmarks = createAsyncThunk(
    "books/getAllBookmarks",
    async () => {
        return await fetchAllBookmarksApi(USER_ID);
    }
)

const { addNewBookmark, deleteBookmark } = bookmarksSlice.actions;

export default bookmarksSlice.reducer;
export const selectBookmarksList = (state: any) => state.bookmarks.list;
export const selectIfBookmarksLoaded = (state: any) => state.bookmarks.fetched;
export const selectIfErrorLoadingBookmarks = (state: any) => state.bookmarks.fetchingError;

export const selectIfBookmarkingDone = (state: any) => state.bookmarks.creatingDone;
export const selectIfRemovingBookmarkDone = (state: any) => state.bookmarks.deletingDone;

export const selectIfBookmarked = (thread_id: string, entry_id: string) => (state: any) => {
    return state.bookmarks.list.find((bm: any) => bm.thread_id === thread_id && bm.entry_id === entry_id);
};